import React from 'react';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from './ui/alert-dialog';
import GenerationTimer from './GenerationTimer';

// Book Recommendations Confirm Modal
export const BooksConfirmModal = ({ open, onClose, onConfirm, loading, showTimer, onTimerDismiss }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Generate Book Recommendation?</AlertDialogTitle>
        <AlertDialogDescription>
          This will generate 1 therapeutic public domain book (pre-1929) recommendation based on the client's Client Synthesis.
          <div className="mt-3 p-3 bg-blue-50 rounded">
            <p className="text-sm font-semibold mb-2">What's included:</p>
            <ul className="text-xs space-y-1">
              <li>• Book details and relevance score</li>
              <li>• 2-minute summary for client discussion</li>
              <li>• Implementation Guide with techniques</li>
              <li>• Download links (Project Gutenberg)</li>
            </ul>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      {(loading || showTimer) && (
        <div className="flex justify-center py-3">
          <GenerationTimer 
            isGenerating={loading} 
            isVisible={showTimer}
            label="Generating" 
          />
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={loading} onClick={() => { onClose(); if (onTimerDismiss) onTimerDismiss(); }}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Now'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Book Success Modal
export const BooksSuccessModal = ({ open, onClose }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="text-green-600">✅ Book Recommendation Generated!</AlertDialogTitle>
        <AlertDialogDescription>
          Your therapeutic book recommendation has been successfully generated.
          <div className="mt-3 p-3 bg-green-50 rounded">
            <p className="text-sm font-semibold mb-2">Next Steps:</p>
            <ul className="text-xs space-y-1">
              <li>• Review the book recommendation below</li>
              <li>• Check the Implementation Guide techniques</li>
              <li>• Use session breakdown for planning</li>
              <li>• Share with client if appropriate</li>
            </ul>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onClose}>View Recommendation</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Movie Recommendations Confirm Modal
export const MoviesConfirmModal = ({ open, onClose, onConfirm, loading, showTimer, onTimerDismiss }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Generate Movie Reflections?</AlertDialogTitle>
        <AlertDialogDescription>
          This will generate therapeutic movie recommendations tailored to the client's psychological profile and language preferences.
          <div className="mt-3 p-3 bg-blue-50 rounded">
            <p className="text-sm font-semibold mb-2">Recommendations include:</p>
            <ul className="text-xs space-y-1">
              <li>• Culturally and linguistically appropriate films</li>
              <li>• Therapeutic themes matching client needs</li>
              <li>• Viewing guides for psychologists</li>
              <li>• Discussion prompts for sessions</li>
            </ul>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      {(loading || showTimer) && (
        <div className="flex justify-center py-3">
          <GenerationTimer 
            isGenerating={loading} 
            isVisible={showTimer}
            label="Generating" 
          />
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={loading} onClick={() => { onClose(); if (onTimerDismiss) onTimerDismiss(); }}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Now'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Movie Success Modal
export const MoviesSuccessModal = ({ open, onClose }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="text-green-600">✅ Movie Reflections Generated!</AlertDialogTitle>
        <AlertDialogDescription>
          Your therapeutic movie recommendations have been successfully generated.
          <div className="mt-3 p-3 bg-green-50 rounded">
            <p className="text-sm font-semibold mb-2">Next Steps:</p>
            <ul className="text-xs space-y-1">
              <li>• Review movie recommendations below</li>
              <li>• Check therapeutic themes and relevance</li>
              <li>• Use discussion prompts in sessions</li>
              <li>• Share with client if appropriate</li>
            </ul>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onClose}>View Recommendations</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
